import { createSelector } from 'reselect';
import { toPercentage, toFactor, toCurrency } from '@/utilities/formatting';
import {
  getFormatting,
  getSummary,
  getValue,
} from '@/pages/CompanyReport/redux/selectors';
import { getAveragesSelector } from '@ducks/averages/selectors';

interface Value {
  marketName: string;
  pe: number;
  pb: number;
  mpe: number;
  mpb: number;
  ipe: number;
  ipb: number;
  peg: number;
  isFund: boolean;
  score: number;
  companyId: string;
  uniqueSymbol: string;
  industryName: string;
  companyName: string;
  tickerSymbol: string;
  intrinsicValue: number;
  sharePrice: number;
  intrinsicDiscount: number;
  intrinsicValueFormatted: string;
  intrinsicDiscountFormatted: string;
  sharePriceFormatted: string;
  keyMetric: string;
  keyMetricTitle: string;
}

interface KeyMetric {
  value: string;
  title: string;
}

const getValuekeyMetric = (
  intrinsicDiscount: number,
  pe: number,
  pb: number
): KeyMetric => {
  let value = '';
  let title = '';
  if (intrinsicDiscount > 0 && intrinsicDiscount < 0.5) {
    value = toPercentage(intrinsicDiscount, 1);
    title = 'Undervalued compared to fair value';
  } else if (intrinsicDiscount > 0.5) {
    value = '>50%';
    title = 'Undervalued compared to fair value';
  } else if (pe > 0) {
    value = toFactor(pe, 2);
    title = 'Price to Earnings (PE) ratio';
  } else if (pb > 0) {
    value = toFactor(pb, 2);
    title = 'Price to Book (PB) ratio';
  }
  return {
    value,
    title,
  };
};

const getValueSelector = createSelector(
  [getSummary, getValue, getFormatting, getAveragesSelector],
  (summary, valueNext, formatting, averages): Value => {
    const valueScore = summary.score.value;
    const valuekeyMetric = getValuekeyMetric(
      valueNext.intrinsicDiscount,
      valueNext.PE,
      valueNext.PB
    );

    const sharePriceFormatted = toCurrency(
      valueNext.sharePrice,
      2,
      formatting.tradingItemCurrencyISO
    );
    const intrinsicValueFormatted = toCurrency(
      valueNext.intrinsicValue,
      2,
      formatting.tradingItemCurrencyISO
    );
    const intrinsicDiscountFormatted = toPercentage(
      Math.abs(valueNext.intrinsicDiscount),
      1
    );
    const { getIndustryAverage, getMarketAverage } = averages;
    return {
      marketName: getMarketAverage('PE').source,
      pe: valueNext.PE,
      pb: valueNext.PB,
      mpe: getMarketAverage('PE').value,
      mpb: getMarketAverage('PB').value,
      ipe: getIndustryAverage('PE').value,
      ipb: getIndustryAverage('PB').value,
      peg: valueNext.PEG,
      score: valueScore,
      isFund: summary.isFund,
      companyId: summary.companyId,
      sharePrice: valueNext.sharePrice,
      sharePriceFormatted,
      companyName: summary.shortName,
      tickerSymbol: summary.tickerSymbol,
      industryName: summary.industryData.secondaryName,
      uniqueSymbol: summary.uniqueSymbol,
      intrinsicValue: valueNext.intrinsicValue,
      intrinsicValueFormatted,
      keyMetric: valuekeyMetric.value,
      keyMetricTitle: valuekeyMetric.title,
      intrinsicDiscount: valueNext.intrinsicDiscount,
      intrinsicDiscountFormatted,
    };
  }
);

export /** Ideally the charts will use this selector to sync the chart image exporter with the App */
const DCFChartSelector = createSelector(
  [getValue, getFormatting],
  (value, formatting) => {
    const sharePriceFormatted = toCurrency(
      value.sharePrice,
      2,
      formatting.tradingItemCurrencyISO
    );

    const intrinsicValueFormatted = toCurrency(
      value.intrinsicValue,
      2,
      formatting.tradingItemCurrencyISO
    );

    const intrinsicDiscountFormatted = toPercentage(value.intrinsicDiscount, 1);

    return {
      sharePrice: value.sharePrice,
      intrinsicValue: value.intrinsicValue,
      intrinsicDiscount: value.intrinsicDiscount,
      sharePriceFormatted,
      intrinsicValueFormatted,
      intrinsicDiscountFormatted,
    };
  }
);

export { getValueSelector as default };
